import { aiJson, aiAvailableSync } from "@/services/ai.service";
import type { CheckResult, CodeQ, Question, Verdict } from "./types";

/**
 * Bewertung – reine Funktionen ohne DOM. Alles, was hier drin steht, ist
 * testbar und wird auch von den Karteikarten (Prüfungsmodus) mitbenutzt.
 */

/* ---------------------------- Textvergleich ---------------------------- */

/**
 * Vergleichsform eines Texts: Kleinschreibung, keine Satzzeichen, ein
 * Leerzeichen zwischen Wörtern. Umlaute bleiben erhalten – „Größe" und
 * „Groesse" sind fachlich dasselbe Wort, aber wer es falsch schreibt, soll
 * das auch sehen; deshalb wird nur die Fuzzy-Stufe darüber nachsichtig.
 */
export function norm(s: string): string {
  return (s ?? "")
    .toLowerCase()
    .replace(/[‘’‚‛']/g, "'")
    .replace(/[“”„"]/g, '"')
    .replace(/[.,;:!?"'`´()[\]{}]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Variante ohne den Inhalt runder Klammern. Damit ist bei
 * „to look (at) something" sowohl „to look at something" als auch
 * „to look something" richtig – Klammerinhalt darf, muss aber nicht.
 */
export function withoutParens(s: string): string {
  return norm((s ?? "").replace(/\([^)]*\)/g, " "));
}

/** Alle Schreibweisen, die für eine Musterlösung als richtig gelten. */
export function acceptedForms(expected: string): string[] {
  const forms = new Set<string>();
  const base = expected ?? "";
  forms.add(norm(base));
  forms.add(withoutParens(base));
  // Klammern nur weglassen, Inhalt aber behalten: „to look (at) sth" ->
  // „to look at sth".
  forms.add(norm(base.replace(/[()]/g, " ")));
  // Mehrere durch „/" oder „;" getrennte Lösungen gelten einzeln.
  for (const part of base.split(/\s*[;/]\s*/)) {
    if (part.trim()) {
      forms.add(norm(part));
      forms.add(withoutParens(part));
    }
  }
  forms.delete("");
  return [...forms];
}

/** Levenshtein-Abstand, gedeckelt – nur für „fast richtig getippt". */
function distance(a: string, b: string): number {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > 3) return 99;
  const prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let last = prev[0];
    prev[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const tmp = prev[j];
      prev[j] = Math.min(
        prev[j] + 1,
        prev[j - 1] + 1,
        last + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
      last = tmp;
    }
  }
  return prev[b.length];
}

export type TextMatch = "exact" | "typo" | "wrong";

/**
 * Freitext gegen Musterlösung(en). „typo" heißt: bis zu zwei Zeichen daneben
 * bei Wörtern ab fünf Buchstaben – das ist ein Tippfehler, kein Wissenslücke,
 * und wird als „fast" gewertet statt als falsch.
 */
export function matchText(input: string, expected: string, accept: string[] = []): TextMatch {
  const got = norm(input);
  if (!got) return "wrong";
  const forms = new Set([...acceptedForms(expected), ...accept.flatMap(acceptedForms)]);
  if (forms.has(got)) return "exact";
  const gotNoParens = withoutParens(input);
  if (forms.has(gotNoParens)) return "exact";
  for (const f of forms) {
    const limit = f.length >= 5 ? 2 : f.length >= 3 ? 1 : 0;
    if (limit && distance(got, f) <= limit) return "typo";
  }
  return "wrong";
}

/* ------------------------------ Zahlen ------------------------------ */

/** „3,5" und „3.5" sind dieselbe Zahl; „1 234" auch. */
export function parseNumber(s: string): number | null {
  const cleaned = (s ?? "")
    .replace(/\s| /g, "")
    .replace(/\.(?=\d{3}\b)/g, "")
    .replace(",", ".");
  const m = cleaned.match(/-?\d+(\.\d+)?([eE][-+]?\d+)?/);
  if (!m) return null;
  const n = Number(m[0]);
  return Number.isFinite(n) ? n : null;
}

/* --------------------------- Antwortformen --------------------------- */

/**
 * Die Antwort des Nutzers, so wie die Renderer sie liefern. Ein Union-Typ
 * wäre hier nur Zeremonie – jeder Typ liest genau das Feld, das er füllt.
 */
export interface Answer {
  index?: number;
  indices?: number[];
  texts?: string[];
  text?: string;
  pairsSolved?: number;
  pairsTotal?: number;
  mistakes?: number;
}

function ok(title: string, detail?: string): CheckResult {
  return { verdict: "correct", title, detail };
}
function partial(title: string, detail?: string): CheckResult {
  return { verdict: "partial", title, detail };
}
function bad(title: string, detail?: string): CheckResult {
  return { verdict: "wrong", title, detail };
}

const PRAISE = ["Das saß!", "Genau richtig.", "Stark.", "Perfekt.", "Richtig!"];
function praise(): string {
  return PRAISE[Math.floor(Math.random() * PRAISE.length)];
}

/**
 * Zentrale Bewertung. Jede Regel steht genau einmal hier – die Renderer
 * sammeln nur die Eingabe ein.
 */
export function checkQuestion(q: Question, a: Answer): CheckResult {
  switch (q.type) {
    case "info":
      return { verdict: "info", title: "Weiter geht's" };

    case "single":
      return a.index === q.correct
        ? ok(praise(), q.explain)
        : bad("Nicht ganz.", q.explain ?? `Richtig wäre: ${q.options[q.correct] ?? "–"}`);

    case "multiple": {
      const picked = new Set(a.indices ?? []);
      const want = new Set(q.correct);
      const hits = [...want].filter((i) => picked.has(i)).length;
      const wrong = [...picked].filter((i) => !want.has(i)).length;
      if (hits === want.size && wrong === 0) return ok(praise(), q.explain);
      if (hits > 0 && wrong === 0) {
        return partial(
          `${hits} von ${want.size} gefunden.`,
          q.explain ?? "Es fehlt noch mindestens eine richtige Antwort."
        );
      }
      return bad(
        "Nicht ganz.",
        q.explain ?? `Richtig: ${q.correct.map((i) => q.options[i]).filter(Boolean).join(", ")}`
      );
    }

    case "fill": {
      const got = a.texts ?? [];
      let hits = 0;
      q.blanks.forEach((sol, i) => {
        if (matchText(got[i] ?? "", sol) !== "wrong") hits++;
      });
      if (hits === q.blanks.length) return ok(praise(), q.explain);
      if (hits > 0) return partial(`${hits} von ${q.blanks.length} Lücken stimmen.`, q.explain);
      return bad("Noch nicht.", q.explain ?? `Richtig: ${q.blanks.join(" · ")}`);
    }

    case "match": {
      const total = a.pairsTotal ?? q.pairs.length;
      const solved = a.pairsSolved ?? 0;
      const mistakes = a.mistakes ?? 0;
      if (solved < total) return bad("Noch nicht alle Paare.", q.explain);
      if (mistakes === 0) return ok(praise(), q.explain);
      return partial(
        `Alle Paare gefunden – mit ${mistakes} Fehlversuch${mistakes === 1 ? "" : "en"}.`,
        q.explain
      );
    }

    case "calc": {
      const got = parseNumber(a.text ?? "");
      if (got === null) return bad("Da fehlt eine Zahl.", q.explain);
      const tol = Math.abs(q.answer) * ((q.tolerance ?? 1) / 100);
      const span = tol || 1e-9;
      if (Math.abs(got - q.answer) <= span) return ok(praise(), q.solution ?? q.explain);
      // Zehnerpotenz daneben: fast immer ein Einheitenfehler, nicht Unwissen.
      if (q.answer !== 0 && [0.001, 0.01, 0.1, 10, 100, 1000].some((f) => Math.abs(got - q.answer * f) <= Math.abs(q.answer * f) * 0.01)) {
        return partial(
          "Der Wert stimmt, die Größenordnung nicht.",
          `Richtig: ${q.answer}${q.unit ? " " + q.unit : ""}. ${q.solution ?? ""}`.trim()
        );
      }
      return bad(
        `Richtig wäre ${q.answer}${q.unit ? " " + q.unit : ""}.`,
        q.solution ?? q.explain
      );
    }

    case "error":
      return a.index === q.badLine
        ? ok("Fehler gefunden!", q.explain)
        : bad(
            "Diese Zeile stimmt.",
            q.explain ?? `Der Fehler steckt in Zeile ${q.badLine + 1}.`
          );

    case "code": {
      const m = matchText(a.text ?? "", q.expected, q.accept ?? []);
      if (m === "exact") return ok(praise(), q.explain);
      if (m === "typo") return partial("Fast – da ist ein Tippfehler drin.", `Erwartet: ${q.expected}`);
      return bad("Noch nicht.", q.explain ?? `Erwartet: ${q.expected}`);
    }

    case "table": {
      const got = a.texts ?? [];
      let hits = 0;
      q.rows.forEach((r, i) => {
        if (norm(got[i] ?? "") === norm(r.correct)) hits++;
      });
      if (hits === q.rows.length) return ok(praise(), q.explain);
      if (hits > 0) return partial(`${hits} von ${q.rows.length} Zeilen stimmen.`, q.explain);
      return bad("Noch nicht.", q.explain);
    }
  }
}

/**
 * Freitext-Bewertung für AFB-3-Aufgaben (Bewerten/Übertragen, Typ "code") per
 * KI statt exaktem Textabgleich – eine ausformulierte Begründung matcht nie
 * wörtlich eine Musterlösung. Bekommt bewusst NUR Frage, Musterlösung und die
 * Antwort des Nutzers – keine Mitschrift/PDFs, damit das mit einem
 * kostenlosen OpenRouter-Modell schnell und günstig bleibt.
 *
 * `checkQuestion` bleibt die exakte, synchrone Prüfung für alles andere
 * (auch AFB 1/2-code-Aufgaben, wo eine feste Antwort richtig ist) und ist
 * der Rückfall, wenn keine KI verfügbar ist oder die Anfrage fehlschlägt.
 */
export async function checkFreeTextWithAi(q: CodeQ, answer: string): Promise<CheckResult> {
  if (!aiAvailableSync()) return checkQuestion(q, { text: answer });
  try {
    const raw = await aiJson<{ verdict: "correct" | "partial" | "wrong"; feedback: string }>(
      "Du bewertest die Antwort einer Schülerin/eines Schülers auf eine offene Prüfungsfrage " +
        "(Anforderungsbereich III: bewerten, begründen, übertragen). Es gibt keine einzig richtige " +
        "Formulierung – bewerte den INHALT gegen die Musterlösung, nicht den Wortlaut. " +
        'Antworte NUR mit JSON: {"verdict": "correct" | "partial" | "wrong", "feedback": "ein kurzer Satz, Deutsch, direkt an die Person gerichtet"}. ' +
        '"correct" nur wenn die Kernaussage(n) der Musterlösung sinngemäß enthalten sind, "partial" wenn nur teilweise, "wrong" wenn nicht.',
      `Frage: ${q.question}\n\nMusterlösung: ${q.expected}\n\nAntwort: ${answer || "(leer)"}`,
      300,
      "small"
    );
    const verdict: Verdict = raw.verdict === "correct" ? "correct" : raw.verdict === "partial" ? "partial" : "wrong";
    return { verdict, title: raw.feedback || praise(), detail: q.explain };
  } catch {
    return checkQuestion(q, { text: answer });
  }
}

/** Punktwert einer Bewertung (für den Verständnis-Score). */
export function verdictScore(v: Verdict): number {
  return v === "correct" ? 1 : v === "partial" ? 0.5 : 0;
}
